import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "project-access" }
const _hoisted_2 = { class: "header-actions" }
const _hoisted_3 = { class: "action-buttons" }
const _hoisted_4 = {
  key: 0,
  class: "loading-container",
  "aria-live": "polite"
}
const _hoisted_5 = {
  key: 1,
  class: "error-container",
  "aria-live": "assertive"
}
const _hoisted_6 = {
  key: 2,
  class: "empty-state"
}
const _hoisted_7 = {
  key: 3,
  class: "grid-container"
}

import { ref, reactive, onMounted, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import ProjectAccessService from '@/services/projectAccess.service'
import AccessInvite from './AccessInvite.vue'
import ConfirmationDialog from './ConfirmationDialog.vue'
import AppDataGrid from '@/components/base/AppDataGrid.vue'

// Define interfaces
interface ClientAccess {
  id: number | string
  clientName: string
  email: string
  invitationDate: string
  status: string | { name: string }
  [key: string]: any
}

interface ColumnDef {
  field: string
  headerName: string
  sortable?: boolean
  minWidth?: number
  flex?: number
  type?: string
  valueFormatter?: (params: any) => string
  cellClass?: (params: any) => string
}

interface Action {
  type: string
  icon: string
  label: string
  tooltip: string
  class: string
  condition?: (data: ClientAccess) => boolean
}

interface Props {
  projectId: string | number
}

interface Emits {
  refresh: []
}

// Props
const deleteIcon = 'M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19M8.46,11.88L9.87,10.47L12,12.59L14.12,10.47L15.53,11.88L13.41,14L15.53,16.12L14.12,17.53L12,15.41L9.88,17.53L8.47,16.12L10.59,14L8.46,11.88M15.5,4L14.5,3H9.5L8.5,4H5V6H19V4H15.5Z'
const resendIcon = 'M2,21L23,12L2,3V10L17,12L2,14V21Z'

// Computed properties for confirmation messages

export default /*@__PURE__*/_defineComponent({
  __name: 'ProjectAccess',
  props: {
    projectId: {}
  },
  emits: ["refresh"],
  setup(__props: any, { emit: __emit }) {

/// <reference types="vue" />
// @ts-ignore
const props = __props

// Emits
const emit = __emit

// Composables
const { t } = useI18n()

// Reactive data
const loading = ref(false)
const error = ref<string | null>(null)
const clientsWithAccess = ref<ClientAccess[]>([])
const showInviteDialog = ref(false)
const isEditMode = ref(false)
const gridApi = ref<any>(null)

// Reactive states for edit and confirmation functionality
const showDeleteConfirmation = ref(false)
const showResendConfirmation = ref(false)
const selectedAccessId = ref<number | string | null>(null)
const selectedAccessData = ref<Partial<ClientAccess>>({})
const isActionLoading = ref(false)

// Material Design icons for confirmation dialogs
const deleteConfirmMessage = computed(() => {
  return t('projects.deleteConfirmMessage', {
    name: selectedAccessData.value.clientName || ''
  })
})

const resendConfirmMessage = computed(() => {
  return t('projects.resendConfirmMessage', {
    name: selectedAccessData.value.clientName || '',
    email: selectedAccessData.value.email || ''
  })
})

// Column definitions for AppDataGrid - make reactive to language changes
const columnDefs = computed<ColumnDef[]>(() => [
  {
    field: 'clientName',
    headerName: t('projects.clientName'),
    sortable: true,
    minWidth: 180,
    flex: 2
  },
  {
    field: 'email',
    headerName: t('projects.emailAddress'),
    sortable: true,
    minWidth: 220,
    flex: 2
  },
  {
    field: 'invitationDate',
    headerName: t('projects.invitationDate'),
    type: 'date',
    sortable: true,
    minWidth: 150,
    flex: 1
  },
  {
    field: 'status',
    headerName: t('projects.invitationStatus'),
    sortable: true,
    minWidth: 150,
    flex: 1,
    valueFormatter: (params: any) => {
      if (!params.value) return ''

      const status = typeof params.value === 'object' ? params.value : String(params.value)
      const statusValue = typeof status === 'object' ? status.name || status : status

      switch(statusValue) {
        case 'ACCEPTED':
          return t('projects.statusAccepted')
        case 'DECLINED':
          return t('projects.statusDeclined')
        case 'PENDING':
        default:
          return t('projects.statusPending')
      }
    },
    cellClass: (params: any) => {
      if (!params.value) return ''

      const status = typeof params.value === 'object' ? params.value : String(params.value)
      const statusValue = typeof status === 'object' ? status.name || status : status

      switch(statusValue) {
        case 'ACCEPTED':
          return 'status-accepted'
        case 'DECLINED':
          return 'status-declined'
        case 'PENDING':
        default:
          return 'status-pending'
      }
    }
  }
])

// Actions configuration for AppDataGrid - make reactive to language changes
const actions = computed<Action[]>(() => [
  {
    name: 'edit',
    icon: 'fas fa-edit',
    label: t('common.edit'),
    tooltip: t('common.edit'),
    class: 'grid-btn-edit'
  },
  {
    name: 'resend',
    icon: 'fas fa-paper-plane',
    label: t('projects.resendInvitation'),
    tooltip: t('projects.resendInvitation'),
    class: 'grid-btn-resend',
    // Only show for pending invitations
    condition: (data: ClientAccess) => {
      const status = typeof data.status === 'object' ? data.status : String(data.status)
      const statusValue = typeof status === 'object' ? status.name || status : status
      return statusValue === 'PENDING'
    }
  },
  {
    name: 'revoke',
    icon: 'fas fa-trash',
    label: t('projects.revokeAccess'),
    tooltip: t('projects.revokeAccess'),
    class: 'grid-btn-delete'
  }
])

const defaultColDef = reactive({
  flex: 1,
  resizable: true
})

// Function to fetch client access data
const fetchClientAccess = async (): Promise<void> => {
  if (!props.projectId) return

  loading.value = true
  error.value = null

  try {
    const data = await ProjectAccessService.getProjectAccess(String(props.projectId))
    clientsWithAccess.value = data || []
  } catch (err) {
    console.error('Failed to load client access data:', err)
    error.value = t('projects.errorLoadingAccess')
  } finally {
    loading.value = false
  }
}

// Load client access data - this is the main function we'll expose and use
const loadClientAccess = async (): Promise<void> => {
  await fetchClientAccess()
}

// Open invite dialog
const openInviteDialog = (): void => {
  // Reset edit mode state for create mode
  isEditMode.value = false
  selectedAccessId.value = null
  selectedAccessData.value = {}
  showInviteDialog.value = true
}

// Handle successful invitation
const handleInviteSent = (): void => {
  loadClientAccess()
  emit('refresh')
}

// Handle actions from AppDataGrid
const handleAction = (action: any, data: any): void => {
  if (!data || !data.id) return

  switch(action.name) {
    case 'edit':
      handleEditAccess(data.id)
      break
    case 'resend':
      handleResendInvitation(data.id)
      break
    case 'revoke':
      handleRevokeAccess(data.id)
      break
    default:
      console.warn('Unknown action:', action.name)
  }
}

// Handle edit access - opens the edit dialog
const handleEditAccess = (clientId: number | string): void => {
  // Find the client by ID
  const client = clientsWithAccess.value.find((c: ClientAccess) => c.id == clientId)
  if (!client) return

  // Set the selected client data
  selectedAccessId.value = clientId
  selectedAccessData.value = { ...client }

  // Open the dialog in edit mode
  isEditMode.value = true
  showInviteDialog.value = true
}

// Handle access updated event from the edit dialog
const handleAccessUpdated = (): void => {
  // Refresh the client access list
  loadClientAccess()
  emit('refresh')
}

// Handle resend invitation - shows confirmation dialog
const handleResendInvitation = (clientId: number | string): void => {
  // Find the client by ID
  const client = clientsWithAccess.value.find((c: ClientAccess) => c.id == clientId)
  if (!client) return

  // Set the selected client data
  selectedAccessId.value = clientId
  selectedAccessData.value = { ...client }

  // Show confirmation dialog
  showResendConfirmation.value = true
}

// Confirm resend invitation action
const confirmResendInvitation = async (): Promise<void> => {
  if (!selectedAccessId.value) return

  isActionLoading.value = true

  try {
    // Call API service to resend invitation
    await ProjectAccessService.resendInvitation(String(props.projectId), String(selectedAccessId.value))

    // Refresh the client access list
    await loadClientAccess()

    // Show success notification (optional - could implement a toast notification system)
    // For now, we'll just close the dialog
    showResendConfirmation.value = false
  } catch (err) {
    console.error('Failed to resend invitation:', err)
    error.value = t('projects.errorResendingInvitation')
  } finally {
    isActionLoading.value = false
  }
}

// Handle revoke access - shows confirmation dialog
const handleRevokeAccess = (clientId: number | string): void => {
  // Find the client by ID
  const client = clientsWithAccess.value.find((c: ClientAccess) => c.id == clientId)
  if (!client) return

  // Set the selected client data
  selectedAccessId.value = clientId
  selectedAccessData.value = { ...client }

  // Show confirmation dialog
  showDeleteConfirmation.value = true
}

// Confirm delete access action
const confirmDeleteAccess = async (): Promise<void> => {
  if (!selectedAccessId.value) return

  isActionLoading.value = true

  try {
    // Call API service to revoke access
    await ProjectAccessService.revokeAccess(String(props.projectId), String(selectedAccessId.value))

    // Refresh the client access list
    await loadClientAccess()

    // Close the dialog
    showDeleteConfirmation.value = false
  } catch (err) {
    console.error('Failed to revoke access:', err)
    error.value = t('projects.errorRevokingAccess')
  } finally {
    isActionLoading.value = false
  }
}

// Watch for projectId changes to reload data
watch(() => props.projectId, (newVal: string | number) => {
  if (newVal) {
    loadClientAccess()
  }
})

// Set up grid events
const onGridReady = (params: any): void => {
  gridApi.value = params.api
}

// Load data when component mounts
onMounted(() => {
  if (props.projectId) {
    loadClientAccess()
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[6] || (_cache[6] = _createElementVNode("h2", {
        class: "component-title",
        id: "access-management-title"
      }, null, -1)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("button", {
          class: "btn btn-primary",
          onClick: openInviteDialog,
          "aria-label": "Invite client"
        }, [
          _cache[5] || (_cache[5] = _createElementVNode("span", { class: "btn-icon" }, "+", -1)),
          _createTextVNode(" " + _toDisplayString(_ctx.$t('projects.inviteClient')), 1)
        ])
      ])
    ]),
    (loading.value && clientsWithAccess.value.length === 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _cache[7] || (_cache[7] = _createElementVNode("div", {
            class: "loading-spinner",
            role: "status",
            "aria-label": "Loading"
          }, null, -1)),
          _createElementVNode("p", null, _toDisplayString(_ctx.$t('common.loading')), 1)
        ]))
      : (error.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _cache[8] || (_cache[8] = _createElementVNode("svg", {
              viewBox: "0 0 24 24",
              width: "48",
              height: "48"
            }, [
              _createElementVNode("path", {
                fill: "var(--error, #e53935)",
                d: "M11,15H13V17H11V15M11,7H13V13H11V7M12,2C6.47,2 2,6.5 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20Z"
              })
            ], -1)),
            _createElementVNode("p", null, _toDisplayString(error.value), 1),
            _createElementVNode("button", {
              onClick: loadClientAccess,
              class: "btn btn-secondary"
            }, _toDisplayString(_ctx.$t('common.retry')), 1)
          ]))
        : _createCommentVNode("", true),
    (clientsWithAccess.value.length === 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          _cache[9] || (_cache[9] = _createElementVNode("svg", {
            viewBox: "0 0 24 24",
            width: "60",
            height: "60",
            "aria-hidden": "true"
          }, [
            _createElementVNode("path", {
              fill: "var(--neutral-300)",
              d: "M12,17A2,2 0 0,0 14,15C14,13.89 13.1,13 12,13A2,2 0 0,0 10,15A2,2 0 0,0 12,17M18,8A2,2 0 0,1 20,10V20A2,2 0 0,1 18,22H6A2,2 0 0,1 4,20V10C4,8.89 4.9,8 6,8H7V6A5,5 0 0,1 12,1A5,5 0 0,1 17,6V8H18M12,3A3,3 0 0,0 9,6V8H15V6A3,3 0 0,0 12,3Z"
            })
          ], -1)),
          _createElementVNode("p", null, _toDisplayString(_ctx.$t('projects.noClientsWithAccess')), 1),
          _createElementVNode("button", {
            class: "btn btn-primary mt-4",
            onClick: openInviteDialog
          }, _toDisplayString(_ctx.$t('projects.inviteClient')), 1)
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_7, [
          _createVNode(AppDataGrid, {
            columnDefs: columnDefs.value,
            rowData: clientsWithAccess.value,
            actions: actions.value,
            defaultColDef: defaultColDef,
            pageSizeOptions: [5, 10, 25, 50, 100],
            pagination: true,
            pageSize: 20,
            suppressPaginationPanel: false,
            animateRows: true,
            showSearch: true,
            searchPlaceholder: _ctx.$t('tasks.placeholders.searchTasks'),
            onGridReady: onGridReady,
            onActionClicked: handleAction,
            gridStyle: "width: 100%;",
            domLayout: "autoHeight"
          }, null, 8, ["columnDefs", "rowData", "actions", "defaultColDef", "searchPlaceholder"])
        ])),
    _createVNode(AccessInvite, {
      show: showInviteDialog.value,
      "onUpdate:show": _cache[0] || (_cache[0] = ($event: any) => ((showInviteDialog).value = $event)),
      projectId: _ctx.projectId,
      isEditMode: isEditMode.value,
      accessId: selectedAccessId.value,
      accessData: selectedAccessData.value,
      onInviteSent: handleInviteSent,
      onAccessUpdated: handleAccessUpdated
    }, null, 8, ["show", "projectId", "isEditMode", "accessId", "accessData"]),
    _createVNode(ConfirmationDialog, {
      show: showDeleteConfirmation.value,
      "onUpdate:show": _cache[1] || (_cache[1] = ($event: any) => ((showDeleteConfirmation).value = $event)),
      title: _ctx.$t('projects.confirmDelete'),
      message: deleteConfirmMessage.value,
      "confirm-button-text": _ctx.$t('projects.deleteAccess'),
      "confirm-button-type": "danger",
      icon: deleteIcon,
      "icon-type": "danger",
      "is-loading": isActionLoading.value,
      onConfirm: confirmDeleteAccess,
      onCancel: _cache[2] || (_cache[2] = ($event: any) => (showDeleteConfirmation.value = false))
    }, null, 8, ["show", "title", "message", "confirm-button-text", "is-loading"]),
    _createVNode(ConfirmationDialog, {
      show: showResendConfirmation.value,
      "onUpdate:show": _cache[3] || (_cache[3] = ($event: any) => ((showResendConfirmation).value = $event)),
      title: _ctx.$t('projects.confirmResend'),
      message: resendConfirmMessage.value,
      "confirm-button-text": _ctx.$t('projects.resendInvitation'),
      "confirm-button-type": "info",
      icon: resendIcon,
      "icon-type": "info",
      "is-loading": isActionLoading.value,
      onConfirm: confirmResendInvitation,
      onCancel: _cache[4] || (_cache[4] = ($event: any) => (showResendConfirmation.value = false))
    }, null, 8, ["show", "title", "message", "confirm-button-text", "is-loading"])
  ]))
}
}

})